import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/button.component';

import { ExtendedReportTemplateList } from '@futrli/views/processors/reports/reportTemplatesList';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';
import { LISTING_PAGE_TABS_HEIGHT } from '@futrli/ui-components/components/listing-page/listing-page-tabs/listing-page-tabs';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { VirtualizedCardList } from '../../../../ui/virtualized-card-list/virtualized-card-list';
import { VirtualizedCardListEmptyState } from '../../../../ui/virtualized-card-list/virtualized-card-list-empty-state';
import { TabErrorMessage } from '../../../../reports/components/tabs/error-message/tab-error-message';
import { DeleteTemplateDialogContainer } from '../../../../reports/components/delete-template-dialog/delete-template-dialog-container';
import { PublishTemplateDialogContainer } from '../../../../reports/components/publish-template-dialog/publish-template-dialog-container';
import { DashboardTemplatesFilters } from './dashboard-templates-filters';
import { useDashboardTemplates } from './hooks/use-dashboard-templates';
import { useStatusFilter, DASHBOARD_TEMPLATES_STORAGE_KEY } from './hooks/use-status-filter';
import { DashboardTemplateRow, ROW_HEIGHT } from './dashboard-template-row';
import { useNavHeight } from '../../../../hooks/useNavHeight';

const statusAccessor = (item: ExtendedReportTemplateList) => item.status;
const nameAccessor = (item: ExtendedReportTemplateList) => item.name;

type TemplateDialogType = 'delete' | 'publish' | null;

interface TemplateDialogState {
  type: TemplateDialogType;
  item: ExtendedReportTemplateList | null;
}

export const DashboardTemplatesTab = () => {
  const intl = useIntl();
  const { navHeight } = useNavHeight();
  const { organisationId = '' } = useParams();
  const { workspaceTemplates, isLoading, errorLoading, isResourceSharingEnabled } = useDashboardTemplates();
  const { dashboard } = useSidebarContext();
  const [activeDialog, setActiveDialog] = useState<TemplateDialogState>({ type: null, item: null });

  const openDialog = useCallback((type: TemplateDialogType, item: ExtendedReportTemplateList) => {
    setActiveDialog({ type, item });
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog({ type: null, item: null });
  }, []);

  const {
    filteredData,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    sortValue,
    sortOptions,
    handleSortChange
  } = useStatusFilter<ExtendedReportTemplateList>({
    data: workspaceTemplates,
    statusAccessor,
    publishedValue: 'published',
    nameAccessor,
    storageKey: DASHBOARD_TEMPLATES_STORAGE_KEY,
    organisationId
  });

  const renderRow = useCallback(
    (item: ExtendedReportTemplateList) => (
      <DashboardTemplateRow
        template={item}
        onDelete={() => openDialog('delete', item)}
        onPublish={() => openDialog('publish', item)}
        isResourceSharingEnabled={isResourceSharingEnabled}
      />
    ),
    [openDialog, isResourceSharingEnabled]
  );

  return (
    <Box>
      <ListingPageHeader
        variant="tab-header"
        title={<FormattedMessage id="predict.dashboards.dashboard_templates" defaultMessage="Dashboard templates" />}
        helpUrl="https://support.futrli.com/en/articles/14597387-dashboard-templates"
        mb={errorLoading ? undefined : 0}
      />
      {errorLoading ? (
        <TabErrorMessage />
      ) : (
        <>
          <DashboardTemplatesFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            sortValue={sortValue}
            onSortChange={handleSortChange}
            sortOptions={sortOptions}
            stickyTop={navHeight + LISTING_PAGE_TABS_HEIGHT}
          />
          <Box mt={2}>
            <VirtualizedCardList<ExtendedReportTemplateList>
              data={filteredData}
              renderRow={renderRow}
              rowHeight={ROW_HEIGHT}
              isLoading={isLoading}
              emptyState={
                <VirtualizedCardListEmptyState
                  title={intl.formatMessage({
                    id: 'reporting.no_dashboard_templates_to_show',
                    defaultMessage: 'No dashboard templates to show'
                  })}
                  description={intl.formatMessage({
                    id: 'reporting.no_dashboard_templates_found_description',
                    defaultMessage: 'Get started with your first dashboard templates'
                  })}
                  action={
                    <Button type="secondary" onClick={() => dashboard.open()}>
                      <FormattedMessage
                        id="reporting.create_dashboard_template"
                        defaultMessage="Create dashboard template"
                      />
                    </Button>
                  }
                />
              }
            />
          </Box>
        </>
      )}
      {activeDialog.type === 'delete' && activeDialog.item && (
        <DeleteTemplateDialogContainer isOpen onClose={closeDialog} template={activeDialog.item} />
      )}
      {activeDialog.type === 'publish' && activeDialog.item && (
        <PublishTemplateDialogContainer isOpen onClose={closeDialog} template={activeDialog.item} />
      )}
    </Box>
  );
};
